'use client';

/**
 * StatCard Component
 *
 * A glassmorphism stat card with accent colors, glow effects, and icon badges.
 * Used for dashboard metrics, KPIs, and status indicators.
 */

import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { alpha, styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type HTMLMotionProps, motion } from 'framer-motion';
import type React from 'react';
import { forwardRef } from 'react';
import { baseColors } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import { motionPresets, transitionPresets } from '../../theme/transitions';

// ============================================================================
// Types
// ============================================================================

export type StatCardColor = 'success' | 'warning' | 'error' | 'primary' | 'secondary' | 'info';
export type StatCardTrend = 'up' | 'down' | 'stable';

export interface StatCardProps {
  /**
   * Title/label for the stat
   */
  title: string;

  /**
   * Main value to display
   */
  value: string | number;

  /**
   * Optional subtitle/description
   */
  subtitle?: string;

  /**
   * Icon to display
   */
  icon?: React.ReactNode;

  /**
   * Trend direction
   */
  trend?: StatCardTrend;

  /**
   * Trend label text
   */
  trendValue?: string;

  /**
   * Accent color for the card
   * @default 'primary'
   */
  color?: StatCardColor;

  /**
   * Enable hover lift animation
   * @default false
   */
  hoverLift?: boolean;

  /**
   * Enable glow on hover
   * @default false
   */
  glowOnHover?: boolean;

  /**
   * Enable entrance animation
   * @default false
   */
  animate?: boolean;

  /**
   * Animation delay in seconds
   * @default 0
   */
  animationDelay?: number;

  /**
   * Click handler
   */
  onClick?: () => void;

  /**
   * Loading state
   * @default false
   */
  loading?: boolean;

  /**
   * Additional styles
   */
  sx?: Record<string, unknown>;
}

// ============================================================================
// Color Utilities
// ============================================================================

const STAT_CARD_COLOR_MAP: Record<StatCardColor, readonly [string, string]> = {
  success: [baseColors.success[500], baseColors.success[400]],
  warning: [baseColors.warning[500], baseColors.warning[400]],
  error: [baseColors.error[500], baseColors.error[400]],
  primary: [baseColors.primary[500], baseColors.primary[400]],
  secondary: [baseColors.secondary[500], baseColors.secondary[400]],
  info: [baseColors.info[500], baseColors.info[400]],
};

const getAccentColorHex = (color: StatCardColor, isDark: boolean): string => {
  const [light, dark] = STAT_CARD_COLOR_MAP[color];
  return isDark ? dark : light;
};

const getIconBgColor = (color: StatCardColor, isDark: boolean): string => {
  const baseColor = getAccentColorHex(color, false);
  return isDark ? alpha(baseColor, 0.15) : alpha(baseColor, 0.1);
};

const getTrendColor = (trend: StatCardTrend): string => {
  switch (trend) {
    case 'up':
      return baseColors.success[400];
    case 'down':
      return baseColors.error[400];
    default:
      return baseColors.neutral[400];
  }
};

// ============================================================================
// Styled Components
// ============================================================================

interface StyledCardProps {
  $isDark: boolean;
  $accentColor: string;
  $glowOnHover: boolean;
  $hoverLift: boolean;
  $clickable: boolean;
}

const StyledCard = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledCardProps>(({ $isDark, $accentColor, $glowOnHover, $hoverLift, $clickable }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    position: 'relative',
    borderRadius: '16px',
    overflow: 'hidden',
    transition: transitionPresets.card,
    cursor: $clickable ? 'pointer' : 'default',
    height: '100%',

    // Background — accent-tinted liquid glass material (colored glass)
    background: $isDark
      ? `linear-gradient(135deg, ${alpha($accentColor, 0.06)} 0%, rgba(28, 28, 30, 0.42) 100%)`
      : `linear-gradient(135deg, ${alpha($accentColor, 0.04)} 0%, rgba(255, 255, 255, 0.50) 100%)`,
    backdropFilter: glass.backdropBlur,
    WebkitBackdropFilter: glass.backdropBlur,
    border: `0.5px solid ${glass.border}`,
    boxShadow: `${glass.shadow}, inset 0 0.5px 0 rgba(255, 255, 255, ${$isDark ? '0.10' : '0.5'})`,

    // Accent bar at top — softened glow
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '3px',
      background: `linear-gradient(90deg, ${$accentColor}, ${alpha($accentColor, 0.15)})`,
      boxShadow: `0 0 20px ${alpha($accentColor, 0.3)}, 0 0 40px ${alpha($accentColor, 0.15)}`,
    },

    // Hover effects
    '&:hover': {
      background: $isDark
        ? `linear-gradient(135deg, ${alpha($accentColor, 0.1)} 0%, rgba(28, 28, 30, 0.52) 100%)`
        : `linear-gradient(135deg, ${alpha($accentColor, 0.06)} 0%, rgba(255, 255, 255, 0.62) 100%)`,
      border: `0.5px solid ${glass.borderHover}`,
      boxShadow: $glowOnHover ? `${glass.shadowHover}, 0 0 30px ${alpha($accentColor, 0.15)}` : glass.shadowHover,
      ...($hoverLift && {
        transform: 'scale(1.01)',
      }),
    },

    '&:active': {
      ...($clickable && {
        transform: 'translateY(-2px)',
      }),
    },
  };
});

const CardContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1.5),
  gap: theme.spacing(1),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2),
    gap: theme.spacing(1.5),
  },
}));

const TitleText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  fontWeight: 600,
  fontSize: '0.75rem', // Minimum accessible size (12px)
  lineHeight: 1.3,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  [theme.breakpoints.up('sm')]: {
    fontSize: '0.75rem',
    letterSpacing: '0.08em',
  },
}));

interface ValueTextProps {
  $accentColor: string;
  $isDark: boolean;
}

const ValueText = styled(Typography, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<ValueTextProps>(({ theme, $accentColor, $isDark }) => ({
  fontWeight: 700,
  marginTop: '4px',
  color: $accentColor,
  textShadow: $isDark ? `0 0 20px ${alpha($accentColor, 0.4)}` : 'none',
  fontSize: '1.125rem', // 18px - reduced from 1.25rem
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  [theme.breakpoints.up('sm')]: {
    fontSize: '1.25rem', // 20px - reduced from 1.5rem
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '1.375rem', // 22px - reduced from 1.75rem
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '1.5rem', // 24px - reduced from 2rem
  },
}));

const SubtitleText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginTop: '4px',
  fontSize: '0.75rem',
  lineHeight: 1.4,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  [theme.breakpoints.up('sm')]: {
    fontSize: '0.75rem',
  },
}));

interface TrendBadgeProps {
  $trendColor: string;
  $isDark: boolean;
}

const TrendBadge = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<TrendBadgeProps>(({ theme, $trendColor, $isDark }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '3px',
  marginTop: '6px',
  paddingLeft: '6px',
  paddingRight: '6px',
  paddingTop: '2px',
  paddingBottom: '2px',
  borderRadius: '4px',
  width: 'fit-content',
  maxWidth: '100%',
  backgroundColor: $isDark ? alpha($trendColor, 0.1) : alpha($trendColor, 0.08),
  [theme.breakpoints.up('sm')]: {
    gap: '4px',
    marginTop: '8px',
    paddingLeft: '8px',
    paddingRight: '8px',
  },
}));

interface IconBoxProps {
  $accentColor: string;
  $iconBg: string;
  $isDark: boolean;
}

const IconBox = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<IconBoxProps>(({ theme, $accentColor, $iconBg, $isDark }) => ({
  padding: '8px',
  borderRadius: '8px',
  backgroundColor: $iconBg,
  color: $accentColor,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: '36px',
  height: '36px',
  boxShadow: $isDark ? `0 0 12px ${alpha($accentColor, 0.15)}` : 'none',
  border: `1px solid ${alpha($accentColor, 0.15)}`,
  '& svg': {
    width: '20px',
    height: '20px',
    filter: $isDark ? `drop-shadow(0 0 2px ${alpha($accentColor, 0.3)})` : 'none',
  },
  [theme.breakpoints.up('sm')]: {
    padding: '10px',
    width: '40px',
    height: '40px',
    '& svg': {
      width: '22px',
      height: '22px',
    },
  },
  [theme.breakpoints.up('md')]: {
    padding: '12px',
    width: '44px',
    height: '44px',
    '& svg': {
      width: '24px',
      height: '24px',
    },
  },
}));

// ============================================================================
// Trend Icon Components
// ============================================================================

const TrendUpIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z" />
  </svg>
);

const TrendDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 18l2.29-2.29-4.88-4.88-4 4L2 7.41 3.41 6l6 6 4-4 6.3 6.29L22 12v6z" />
  </svg>
);

const TrendFlatIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M22 12l-4-4v3H3v2h15v3z" />
  </svg>
);

// ============================================================================
// Skeleton Component
// ============================================================================

export function StatCardSkeleton() {
  return (
    <StyledCard
      $isDark={false}
      $accentColor={baseColors.neutral[400]}
      $glowOnHover={false}
      $hoverLift={false}
      $clickable={false}
    >
      <CardContent>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton variant="text" width="70%" sx={{ maxWidth: 100 }} />
          <Skeleton variant="text" width="50%" height={32} sx={{ maxWidth: 80, mt: 0.5 }} />
          <Skeleton variant="text" width="60%" sx={{ maxWidth: 60, mt: 0.5 }} />
        </Box>
        <Skeleton
          variant="rounded"
          sx={{
            borderRadius: 2,
            flexShrink: 0,
            width: { xs: 36, sm: 40, md: 44 },
            height: { xs: 36, sm: 40, md: 44 },
          }}
        />
      </CardContent>
    </StyledCard>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export const StatCard = forwardRef<HTMLDivElement, StatCardProps>(
  (
    {
      title,
      value,
      subtitle,
      icon,
      trend,
      trendValue,
      color = 'primary',
      hoverLift = false,
      glowOnHover = false,
      animate = false,
      animationDelay = 0,
      onClick,
      loading = false,
      sx,
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const accentHex = getAccentColorHex(color, isDark);
    const iconBg = getIconBgColor(color, isDark);
    const trendColor = trend ? getTrendColor(trend) : baseColors.neutral[400];

    // Animation variants
    const animationProps: HTMLMotionProps<'div'> = animate
      ? {
          initial: motionPresets.fadeInUp.initial,
          animate: motionPresets.fadeInUp.animate,
          transition: {
            ...motionPresets.fadeInUp.transition,
            delay: animationDelay,
          },
        }
      : {};

    const getTrendIcon = () => {
      switch (trend) {
        case 'up':
          return <TrendUpIcon />;
        case 'down':
          return <TrendDownIcon />;
        case 'stable':
          return <TrendFlatIcon />;
        default:
          return null;
      }
    };

    if (loading) {
      return <StatCardSkeleton />;
    }

    return (
      <Box sx={{ height: '100%', ...sx }}>
        <StyledCard
          ref={ref}
          $isDark={isDark}
          $accentColor={accentHex}
          $glowOnHover={glowOnHover}
          $hoverLift={hoverLift}
          $clickable={!!onClick}
          onClick={onClick}
          {...animationProps}
        >
          <CardContent>
            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <TitleText variant="caption">{title}</TitleText>
              <ValueText $accentColor={accentHex} $isDark={isDark}>
                {value}
              </ValueText>
              {subtitle && <SubtitleText variant="caption">{subtitle}</SubtitleText>}
              {trend && trendValue && (
                <TrendBadge $trendColor={trendColor} $isDark={isDark}>
                  <Box sx={{ color: trendColor, display: 'flex' }}>{getTrendIcon()}</Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: trendColor }}>
                    {trendValue}
                  </Typography>
                </TrendBadge>
              )}
            </Box>
            {icon && (
              <IconBox $accentColor={accentHex} $iconBg={iconBg} $isDark={isDark}>
                {icon}
              </IconBox>
            )}
          </CardContent>
        </StyledCard>
      </Box>
    );
  },
);

StatCard.displayName = 'StatCard';

export default StatCard;
